import React from 'react'
import { Box, Text, Flex } from '@chakra-ui/react'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { PlusCircle } from 'lucide-react'
import useEmailDesignerStore from './emailDesignerStore'
import CanvasBlock from './CanvasBlock'
import { PRIMARY_BG } from '../../../../theme/brand';

const ACCENT = PRIMARY_BG

function EmptyState() {
  return (
    <Box
      py={20} px={8}
      display="flex" alignItems="center" justifyContent="center"
      flexDir="column" gap={5}
    >
      <Box
        w="80px" h="80px" borderRadius="20px"
        style={{ background: `linear-gradient(135deg, ${ACCENT}15 0%, ${ACCENT}25 100%)` }}
        border="1.5px dashed" borderColor={`${ACCENT}40`}
        display="flex" alignItems="center" justifyContent="center"
        shadow={`0 4px 20px ${ACCENT}15`}
      >
        <PlusCircle size={32} color={ACCENT} strokeWidth={1.5} />
      </Box>
      <Box textAlign="center">
        <Text fontSize="14px" color="gray.500" fontWeight="700">Start building your email</Text>
        <Text fontSize="12px" color="gray.400" mt={2} lineHeight="1.6">
          Drag blocks from the left panel<br />or click any block to add it here
        </Text>
      </Box>
      <Flex gap={4} align="center" opacity={0.6}>
        {['Heading', 'Image', 'Button'].map(name => (
          <Box
            key={name}
            px={2.5} py={1}
            bg="white"
            borderRadius="6px"
            border="1px solid" borderColor="gray.200"
            shadow="0 1px 3px rgba(0,0,0,0.04)"
          >
            <Text fontSize="10px" color="gray.500" fontWeight="600">{name}</Text>
          </Box>
        ))}
        <Text fontSize="10px" color="gray.400">+ more</Text>
      </Flex>
    </Box>
  )
}

function EmailCanvas() {
  const { blocks, emailSettings, selectBlock } = useEmailDesignerStore()
  const { setNodeRef, isOver } = useDroppable({ id: 'canvas-root' })

  const bgStyle = emailSettings.backgroundType === 'gradient'
    ? { background: emailSettings.backgroundColor || '#F7F9FC' }
    : { backgroundColor: emailSettings.backgroundColor || '#F7F9FC' }

  const bodyBgStyle = emailSettings.contentBackgroundType === 'gradient'
    ? { background: emailSettings.contentBackground || '#ffffff' }
    : { backgroundColor: emailSettings.contentBackground || '#ffffff' }

  return (
    <Box
      flex={1}
      h="100%"
      overflowY="auto"
      overflowX="hidden"
      css={{
        background: '#f0f2f5',
        backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
        backgroundSize: '20px 20px',
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: '#d1d5db', borderRadius: '4px' },
      }}
      onClick={() => selectBlock(null)}
    >
      {/* Page background */}
      <Box
        minH="100%"
        display="flex"
        flexDir="column"
        alignItems="center"
        py="40px"
        px="16px"
        style={bgStyle}
      >
        {/* Email card */}
        <Box
          ref={setNodeRef}
          w={`${emailSettings.contentWidth || 600}px`}
          maxW="100%"
          minH="400px"
          shadow={isOver
            ? `0 0 0 3px ${ACCENT}40, 0 8px 40px rgba(0,0,0,0.12)`
            : '0 4px 32px rgba(0,0,0,0.1), 0 1px 6px rgba(0,0,0,0.05)'
          }
          borderRadius="6px"
          overflow="visible"
          position="relative"
          outline={isOver ? `2px dashed ${ACCENT}` : 'none'}
          outlineOffset="4px"
          transition="all 0.15s"
          style={bodyBgStyle}
        >
          <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
            {blocks.length === 0
              ? <EmptyState />
              : blocks.map(block => <CanvasBlock key={block.id} block={block} />)
            }
          </SortableContext>
        </Box>

        {/* Width label */}
        <Flex align="center" gap={2} mt={5}>
          <Box h="1px" flex={1} maxW="48px" bg="gray.400" opacity={0.3} />
          <Box
            px={2.5} py={1}
            bg="white" borderRadius="6px"
            border="1px solid" borderColor="gray.200"
            shadow="0 1px 4px rgba(0,0,0,0.06)"
          >
            <Text fontSize="10px" color="gray.500" fontWeight="700">
              {emailSettings.contentWidth || 600}px
            </Text>
          </Box>
          <Box h="1px" flex={1} maxW="48px" bg="gray.400" opacity={0.3} />
        </Flex>
      </Box>
    </Box>
  )
}

export default EmailCanvas
